import React, { useState } from "react";
import {
  IconButton,
  Popover,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Box,
  CircularProgress,
} from "@mui/material";
import {
  MoreVertIcon,
  FiEdit2,
  LuRefreshCw,
  FiUnlock,
  FiLock,
  LuTrash2,
  LuPrinter,
  FaFileExcel,
} from "@/Components/Icons/Icons";
import ConfirmDialog from "@/Components/Dialog/ConfirmDialog";
import {
  useGetDashboardQuery,
  useToggleLockMutation,
  useDeleteJobMutation,
} from "../../../redux/ExportPage/Dashboard/DashboardSlice";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const ActionCellRenderer = (props) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [openLock, setOpenLock] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [confirmDialogConfig, setConfirmDialogConfig] = useState({});
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [lockLoading, setLockLoading] = useState(false);
  const { handleRowClick } = props; // destructure from props

  const {
    setShowSuccessDialog,
    setSuccessDialogConfig,
    setCurrentRowData,
    setSuccessSource,
  } = props;

  const { refetch } = props;
  const [toggleLock] = useToggleLockMutation();
  const [deleteJob] = useDeleteJobMutation();
  const isMenuOpen = Boolean(anchorEl);
  const handleClick = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleAction = (action) => {
    switch (action) {
      case "Edit":
        handleRowClick(props.data); // 🔥 call your dashboard navigation
        break;
      case "Refresh":
        props.onRefresh?.(props.data);
        break;
      case "Lock":
        handleLock();
        break;
      case "Delete":
        openDeleteConfirmation();
        break;
        case "Print":
          props.onPrint?.(props.data); // calls GridExample's print
          break;
      case "ExportToExcel":
        handleExportToExcel(props.data);
        break;
      default:
        break;
    }
    handleClose();
  };

  const openDeleteConfirmation = () => {
    setConfirmDialogConfig({
      label: "Delete Job",
      title: "Tripathlogistics.mylogisys.com says",
      jobNo: props.data.exporterJobNumber,
      subtitle: "Are you sure you want to delete?",
      inputLabel: "Reason",
      confirmLabel: "Ok",
      loadingText: "Deleting...",
      cancelLabel: "Cancel",
    });
    setOpenDelete(true);
  };

  const handleConfirmDelete = async () => {
    setOpenDelete(false);
    setDeleteLoading(true);
    try {
      await deleteJob({
        exporterMasterId: props.data.exporterMasterId,
        requestId: Date.now().toString(),
      }).unwrap();
      
      await refetch(); // ✅ refresh latest data from server
      setSuccessDialogConfig({
        label: "Delete Job",
        title: "Tripathlogistics.mylogisys.com says",
        subtitle: (
          <>
            The job{" "}
            <strong style={{ fontSize: "1rem", color: "var(--brand-900)" }}>
              {props.data.exporterJobNumber}
            </strong>{" "}
            has been deleted successfully.
          </>
        ),
        buttonText: "OK",
        status: "success",
      });
      setCurrentRowData(props.data);
      setSuccessSource("delete");
      setShowSuccessDialog(true);
    } catch (error) {
      setSuccessDialogConfig({
        label: "Error",
        title: "Tripathlogistics.mylogisys.com says",
        subtitle: <>Failed to delete job. Please try again.</>,
        buttonText: "OK",
        status: "error",
      });
      setCurrentRowData(null);
      setSuccessSource("error");
      setShowSuccessDialog(true);
    } finally {
      setDeleteLoading(false);
      setOpenDelete(false);
    }
  };
  const handleConfirmLock = async () => {
    setLockLoading(true); // show spinner in confirm dialog
    try {
      const exporterMasterId = props.data?.exporterMasterId;
      const newStatus = props.data.exportLockStatus === "L" ? "U" : "L";

      await toggleLock({
        exporterMasterId,
        requestId: Date.now().toString(),
      }).unwrap();

      // success config
      setSuccessDialogConfig({
        label: newStatus === "L" ? "Lock" : "Unlock",
        title: "Tripathlogistics.mylogisys.com says",
        subtitle: (
          <>
            The job{" "}
            <strong style={{ fontSize: "1rem", color: "var(--brand-900)" }}>
              {props.data.exporterJobNumber}
            </strong>{" "}
            has been {newStatus === "L" ? "locked" : "unlocked"} successfully.
          </>
        ),
        buttonText: "OK",
        status: "success",
      });
      setCurrentRowData({ ...props.data, newStatus });
      setSuccessSource("lock");
      setShowSuccessDialog(true);
    } catch (error) {
      setSuccessDialogConfig({
        label: "Error",
        title: "Tripathlogistics.mylogisys.com says",
        subtitle: <>Failed to update lock status. Please try again.</>,
        buttonText: "OK",
        status: "error",
      });
      setCurrentRowData(null);
      setSuccessSource("error");
      setShowSuccessDialog(true);
    } finally {
      setLockLoading(false);
      setOpenLock(false); 
      await refetch(); // ✅ refresh latest data from server
    }
  };

  const handleLock = () => {
    const isLocked = props.data?.exportLockStatus === "L";
    const jobNo = props.data?.exporterJobNumber;
    const lockTimestamp = props.data?.lockTime || new Date().toLocaleString();

    setConfirmDialogConfig({
      label: isLocked ? "Unlock" : "Lock",
      Note: isLocked ? (
        <>
          Are you sure you want to <strong>unlock</strong> this job?
        </>
      ) : (
        "NOTE: Editing cannot be done for locked jobs."
      ),
      subtitle: isLocked ? (
        <>
          The job <strong>"{jobNo}"</strong> was locked on{" "}
          <strong style={{ fontSize: "1rem", color: "var(--brand-900)" }}>
            {lockTimestamp}
          </strong>
          .
        </>
      ) : (
        <>
          Are you sure you want to lock the jtob{" "}
          <strong style={{ fontSize: "1rem", color: "var(--brand-900)" }}>
            "{jobNo}"
          </strong>{" "}
          for operations?
        </>
      ),
      confirmLabel: isLocked ? "Unlock" : "Lock",
      // loadingText:lockLoading ? "Unlocking..." : "Locking...",
      cancelLabel: "Cancel",
      setReason: null,
    });

    setOpenLock(true);
  };

  const handlePrint = (rowData) => {
    const printWindow = window.open("", "_blank", "width=800,height=600");
    if (!printWindow) return;

    // exclude exporterMasterId from print
    const filteredData = Object.fromEntries(
      Object.entries(rowData).filter(([key]) => key !== "exporterMasterId")
    );

    printWindow.document.write(`
      <html>
        <head>
          <title>Print Job - ${rowData.exporterJobNumber}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h2 { color: #333; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ccc; padding: 8px; text-align: left; }
            th { background-color: #f4f4f4; }
          </style>
        </head>
        <body>
          <h2>Job Details</h2>
          <table>
            ${Object.entries(filteredData)
              .map(
                ([key, value]) => `
                  <tr>
                    <th>${key}</th>
                    <td>${value ?? ""}</td>
                  </tr>`
              )
              .join("")}
          </table>
          <script>
            window.print();
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();
  };

  const handleExportToExcel = (rowData) => {
    const worksheet = XLSX.utils.json_to_sheet([rowData]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Job Data");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, `Job_${rowData.exporterJobNumber || "Export"}.xlsx`);
  };

  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation(); // ❌ stop row click
          handleClick(e); // ✅ open menu
        }}
        sx={{
          width: 36,
          height: 36,
          borderRadius: "50%",
          backgroundColor: isMenuOpen ? "var(--brand-300)" : "var(--brand-0)",
        }}
      >
        <MoreVertIcon style={{ fontSize: 20, color: "var(--brand-600)" }} />
      </IconButton>

      <Popover
        open={isMenuOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            p: 1,
            minWidth: 250,
            border: "1px solid var(--gray-400)",
            borderRadius: 3,
          },
        }}
      >
        <Box display="flex" flexDirection="column" gap={1.5}>
          {props.data.exportLockStatus !== "L" && (
            <MenuItem
              onClick={() => handleAction("Edit")}
              sx={{
                "&:hover": { backgroundColor: "var(--brand-200)" },
                borderRadius: 1,
              }}
            >
              <ListItemIcon>
                <FiEdit2 size={18} />
              </ListItemIcon>
              <ListItemText>Edit</ListItemText>
            </MenuItem>
          )}

          <MenuItem
            onClick={() => handleAction("Refresh")}
            sx={{
              "&:hover": { backgroundColor: "var(--brand-200)" },
              borderRadius: 1,
            }}
          >
            <ListItemIcon>
              <LuRefreshCw size={18} />
            </ListItemIcon>
            <ListItemText>Refresh</ListItemText>
          </MenuItem>

          <MenuItem
            onClick={() => handleAction("Lock")}
            disabled={lockLoading} // disable while loading
            sx={{
              "&:hover": { backgroundColor: "var(--brand-200)" },
              borderRadius: 1,
            }}
          >
            <ListItemIcon sx={{ pointerEvents: "none" }}>
              {lockLoading ? (
                <CircularProgress size={18} />
              ) : props.data.exportLockStatus === "L" ? (
                <FiUnlock size={18} />
              ) : (
                <FiLock size={18} />
              )}
            </ListItemIcon>
            <ListItemText>
              {lockLoading
                ? props.data.exportLockStatus === "L"
                  ? "Unlocking..."
                  : "Locking..."
                : props.data.exportLockStatus === "L"
                  ? "Unlock"
                  : "Lock"}
            </ListItemText>
          </MenuItem>

          <MenuItem
            onClick={() => handleAction("Delete")}
            sx={{
              "&:hover": { backgroundColor: "var(--brand-200)" },
              borderRadius: 1,
            }}
          >
            <ListItemIcon>
              <LuTrash2 size={18} />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>

          <MenuItem
            onClick={() => handleAction("Print")}
            sx={{
              "&:hover": { backgroundColor: "var(--brand-200)" },
              borderRadius: 1,
            }}
          >
            <ListItemIcon>
              <LuPrinter size={18} />
            </ListItemIcon>
            <ListItemText>Print</ListItemText>
          </MenuItem>

          <MenuItem
            onClick={() => handleAction("ExportToExcel")}
            sx={{
              "&:hover": { backgroundColor: "var(--brand-200)" },
              borderRadius: 1,
            }}
          >
            <ListItemIcon>
              <FaFileExcel size={18} color="var(--green-400)" />
            </ListItemIcon>
            <ListItemText>Export to Excel</ListItemText>
          </MenuItem>
        </Box>
      </Popover>

      {/* Delete Confirm */}
      <ConfirmDialog
        open={openDelete}
        onClose={() => setOpenDelete(false)}
        onConfirm={handleConfirmDelete}
        loading={deleteLoading}
        reason={deleteReason} // ✅ bind directly
        setReason={setDeleteReason}
        loadingText="Deleting..."
        {...confirmDialogConfig}
      />

      {/* Lock Confirm */}
      <ConfirmDialog
        open={openLock}
        onClose={() => setOpenLock(false)}
        onConfirm={handleConfirmLock}
        loading={lockLoading}
        loadingText={
          props.data?.exportLockStatus === "L" ? "Unlocking..." : "Locking..."
        }
        {...confirmDialogConfig}
      />
    </>
  );
};

export default ActionCellRenderer;

